import { client } from '@/shared/api/generated'

export interface WebAuthnCredentialInfo {
  name: string
  created_at: string
}

export const webauthnApi = {
  registerBegin: async (): Promise<PublicKeyCredentialCreationOptions> => {
    const { data } = await client.post<{ publicKey: PublicKeyCredentialCreationOptions }>('/webauthn/register/begin')
    return data.publicKey ?? data as unknown as PublicKeyCredentialCreationOptions
  },

  registerFinish: async (name: string, credential: PublicKeyCredential): Promise<void> => {
    const attestation = credential.response as AuthenticatorAttestationResponse
    const body = {
      id: credential.id,
      rawId: bufToBase64url(credential.rawId),
      type: credential.type,
      response: {
        attestationObject: bufToBase64url(attestation.attestationObject),
        clientDataJSON: bufToBase64url(attestation.clientDataJSON),
      },
    }
    await client.post(`/webauthn/register/finish?name=${encodeURIComponent(name)}`, body)
  },

  loginBegin: async (tempToken: string): Promise<PublicKeyCredentialRequestOptions> => {
    const { data } = await client.post<{ publicKey: PublicKeyCredentialRequestOptions }>(
      '/webauthn/login/begin',
      {},
      { headers: { Authorization: `Bearer ${tempToken}` } },
    )
    return data.publicKey ?? data as unknown as PublicKeyCredentialRequestOptions
  },

  loginFinish: async (tempToken: string, credential: PublicKeyCredential): Promise<{ token: string }> => {
    const assertion = credential.response as AuthenticatorAssertionResponse
    const body = {
      id: credential.id,
      rawId: bufToBase64url(credential.rawId),
      type: credential.type,
      response: {
        authenticatorData: bufToBase64url(assertion.authenticatorData),
        clientDataJSON: bufToBase64url(assertion.clientDataJSON),
        signature: bufToBase64url(assertion.signature),
        userHandle: assertion.userHandle ? bufToBase64url(assertion.userHandle) : undefined,
      },
    }
    const { data } = await client.post<{ token: string; status: string }>(
      '/webauthn/login/finish',
      body,
      { headers: { Authorization: `Bearer ${tempToken}` } },
    )
    return { token: data.token }
  },

  listCredentials: async (): Promise<WebAuthnCredentialInfo[]> => {
    const { data } = await client.get<WebAuthnCredentialInfo[]>('/webauthn/credentials')
    return data
  },

  removeCredential: async (name: string): Promise<void> => {
    await client.delete(`/webauthn/credentials?name=${encodeURIComponent(name)}`)
  },
}

function bufToBase64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let str = ''
  for (const b of bytes) str += String.fromCharCode(b)
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
